import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { useCart } from "@/context/CartContext";

// Flat editorial product card. `attached` removes the outer border for hairline grids (gap-px bg-ink).
const ProductCard = ({ product, attached = false }) => {
  const { t, tr } = useLang();
  const { add } = useCart();
  const soldOut = Boolean(product.soldOut);

  return (
    <div
      className={`group flex flex-col h-full bg-cream ${attached ? "" : "border border-ink"}`}
      data-testid={`product-card-${product.id}`}
    >
      <Link
        to={`/product/${product.id}`}
        className="relative block aspect-square overflow-hidden border-b border-ink"
        style={{ backgroundColor: product.tile || "#F9F8F6" }}
      >
        <span className="absolute top-3 left-3 z-10 kicker bg-cream border border-ink px-2 py-0.5">
          {product.number}
        </span>
        {soldOut && (
          <span
            className="absolute top-3 right-3 z-10 kicker bg-sand text-ink/60 border border-ink/40 px-2 py-0.5"
            data-testid={`soldout-badge-${product.id}`}
          >
            {t.shop.soldOut}
          </span>
        )}
        {product.isNew && !soldOut && (
          <span
            className="absolute top-3 right-3 z-10 kicker bg-pink text-cream border border-ink px-2 py-0.5"
            data-testid={`newdrop-badge-${product.id}`}
          >
            {t.shop.newDrop}
          </span>
        )}
        <img
          src={product.image}
          alt={tr(product.name)}
          loading="lazy"
          className={`w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03] ${
            soldOut ? "grayscale-[45%] opacity-75" : ""
          }`}
        />
      </Link>

      <div className="p-5 flex flex-col flex-1">
        <p className="kicker" style={{ color: soldOut ? "#9a958c" : product.accent }}>
          {tr(product.enemy)}
        </p>
        <h3 className={`display uppercase text-2xl leading-none mt-1.5 ${soldOut ? "line-through text-ink/40" : ""}`}>
          {tr(product.name)}
        </h3>
        <p className="text-sm text-ink/60 mt-2 flex-1">{tr(product.short)}</p>

        <div className="flex items-center justify-between mt-4 gap-3">
          <span className={`display text-2xl ${soldOut ? "line-through text-ink/40" : ""}`}>€{product.price}</span>
          {soldOut ? (
            <button disabled className="btn-disabled h-11 px-4 text-xs" data-testid={`add-to-cart-${product.id}`}>
              {t.shop.backSoon}
            </button>
          ) : (
            <button
              onClick={() => add(product)}
              data-testid={`add-to-cart-${product.id}`}
              className="btn-accent h-11 px-4 text-xs"
            >
              <Plus className="w-4 h-4" strokeWidth={2.5} /> {t.addToCart}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
